/**
 * External Imports
 */
import { FC, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';

import { RootState } from '../../../../redux/store';
import { openModal, setModalType } from '../../../../redux/actions';
import { useGetSassTokenQuery } from '../../../../redux/apis/Sass.api';
import {
  usePostRemoveDelegateFromUserMutation,
  useGetEmployeeConfigQuery
} from '../../../../redux/apis/Users.api';

import { handleBlobPicUrl, userProfilePicUrl } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { AuthUserProps, Employee } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';
import {
  AddDelegatesBtn,
  DelegateAvatar,
  DelegateNameInTable,
  DelegatesConfigContainer,
  DelegateSlot,
  Meta,
  RemoveButton,
  TextInTable
} from './MyDelegatesTable.styles';

/**
 * Static File Imports
 */
import defaultImage from '../../../../assets/images/defaultImage.png';

interface MyDelegatesProps {
  authorizedUser: AuthUserProps;
}
const MyDelegatesListTable: FC<MyDelegatesProps> = ({ authorizedUser }) => {
  const dispatch = useDispatch();

  const [sortKey, setSortKey] = useState<string>('lastName');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Delegate');

  const {
    data: { delegatedToEmployees: myDelegatesList = [] } = {},
    // isError: isGetEmployeeConfigError,
    // isFetching: isGetEmployeeConfigFetching,
    isLoading: isGetEmployeeConfigLoading
  } = useGetEmployeeConfigQuery(authorizedUser?.hcaid ?? '', {
    refetchOnMountOrArgChange: 120
  });
  const [
    removeDelegateFromUser
    // {
    //   isError: isRemoveDelegateFromUserError,
    //   isLoading: isRemoveDelegateFromUserLoading,
    //   isSuccess: isRemoveDelegateFromUserSuccess
    // }
  ] = usePostRemoveDelegateFromUserMutation();

  const handleRemoveEmployee = async (employee: Employee) => {
    const payload = {
      userId: authorizedUser.hcaid,
      delegateId: employee.hcaid
    };
    await removeDelegateFromUser(payload);
  };
  const {
    data: sasTokenData,
    // isError: isGetSassTokenError,
    isFetching: isGetSassTokenFetching
    // isLoading: isGetSassTokenLoading
  } = useGetSassTokenQuery();

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Delegate':
        key = 'lastName';
        break;
      case 'Department':
        key = 'department';
        break;
      case 'Title':
        key = 'title';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const openEditModal = () => {
    dispatch(setModalType('Add Delegates'));
    dispatch(openModal());
  };

  const filteredList = useMemo(() => {
    const stateCopy = [...myDelegatesList];

    stateCopy.sort((a: Employee, b: Employee) =>
      a[sortKey as keyof Employee]?.toLowerCase() >
      b[sortKey as keyof Employee]?.toLowerCase()
        ? sortDir === 'asc'
          ? 1
          : -1
        : sortDir === 'asc'
        ? -1
        : 1
    );

    return stateCopy;
  }, [myDelegatesList, sortKey, sortDir]);

  // columns="{'Delegate': '34%','Department':'27%', 'Title': '27%', 'Delete': '12%'}"
  const headers: JSX.Element[] = [
    <CellContainer
      key="Delegate"
      id="My-Delegate-List-Table-Delegate-Column"
      unit="column"
      colWidth={34}
      direction={!(active === 'Delegate') ? 'none' : sortDir}
      sort
    >
      Delegate
    </CellContainer>,
    <CellContainer
      key="Department"
      id="My-Delegate-List-Table-Department-Column"
      unit="column"
      colWidth={27}
      direction={!(active === 'Department') ? 'none' : sortDir}
      sort
    >
      Department
    </CellContainer>,
    <CellContainer
      key="Title"
      id="My-Delegate-List-Table-Title-Column"
      unit="column"
      colWidth={27}
      direction={!(active === 'Title') ? 'none' : sortDir}
      sort
    >
      Title
    </CellContainer>,
    <CellContainer
      key="Delete"
      id="My-Delegate-List-Table-Delete-Column"
      unit="column"
      colWidth={12}
      direction="none"
    />
  ];

  const rows = filteredList?.map((delegate: Employee, i: number) => {
    return (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`Delegated-To-Me-List-Row-${i}`}
        id={`Delegated-To-Me-List-Row-${delegate.hcaid}`}
        unit="row"
        size="default"
        height="5em"
      >
        <CellContainer unit="column" colWidth={34}>
          <DelegateSlot>
            {sasTokenData ? (
              <DelegateAvatar
                src={
                  handleBlobPicUrl(
                    delegate.pictureUrl,
                    sasTokenData.sasToken
                  ) ?? userProfilePicUrl(delegate.hcaid, sasTokenData.sasToken)
                }
                alt="Delegate Image"
                onError={e => {
                  e.currentTarget.src = defaultImage;
                  return null;
                }}
              />
            ) : (
              <DelegateAvatar src={defaultImage} alt="Default Image" />
            )}
            <div>
              <DelegateNameInTable className="my-0">{`${delegate.firstName} ${delegate.lastName}`}</DelegateNameInTable>
              <TextInTable className="my-0">{delegate?.hcaid}</TextInTable>
            </div>
          </DelegateSlot>
        </CellContainer>
        <CellContainer unit="column" colWidth={27}>
          <Meta>
            <TextInTable className="my-0">
              {delegate.department || 'Unknown'}
            </TextInTable>
          </Meta>
        </CellContainer>
        <CellContainer unit="column" colWidth={27}>
          <Meta>
            <TextInTable className="my-0">
              {delegate.title || 'Unknown'}
            </TextInTable>
          </Meta>
        </CellContainer>
        <CellContainer unit="column" colWidth={12}>
          <RemoveButton
            color="orange"
            kind="secondary"
            size="small"
            onClick={() => handleRemoveEmployee(delegate)}
          >
            Remove
          </RemoveButton>
        </CellContainer>
      </StyledNeuTableRow>
    );
  });

  return (
    <div className="d-flex flex-column w-100">
      <DelegatesConfigContainer>
        <div className="d-flex align-items-center">
          <AddDelegatesBtn
            id="Add-Delegates-Button"
            data-testid="add-delegates-button"
            color="blue"
            kind="primary"
            size="small"
            onClick={openEditModal}
          >
            Add Delegates
          </AddDelegatesBtn>
        </div>
      </DelegatesConfigContainer>
      <Table
        customClass="my-delegates-table"
        headers={headers}
        rows={rows}
        loading={isGetEmployeeConfigLoading || isGetSassTokenFetching}
        type="delegate-list"
        onSort={(e: any) => handleSort(e.detail.label)}
      />
    </div>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser
  };
};
export default connect(mapReduxStateToProps)(MyDelegatesListTable);
